import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations } from "@/i18n";
import { api } from "@/lib/api";

const AppContext = createContext(null);
const IQD_RATE = 1310;

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("rfd_lang") || "ar");
  const [currency, setCurrency] = useState(() => localStorage.getItem("rfd_currency") || "USD");
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [settings, setSettings] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const t = translations[lang];

  useEffect(() => {
    const dir = translations[lang].dir;
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", lang);
    localStorage.setItem("rfd_lang", lang);
  }, [lang]);

  useEffect(() => { localStorage.setItem("rfd_currency", currency); }, [currency]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith("#session_id=")) {
      const sid = new URLSearchParams(hash.slice(1)).get("session_id");
      window.history.replaceState(null, "", window.location.pathname);
      api.post("/auth/session", { session_id: sid }, { withCredentials: true })
        .then((r) => { localStorage.setItem("rfd_token", r.data.token); setUser(r.data.user); })
        .catch(() => localStorage.removeItem("rfd_token"))
        .finally(() => setAuthReady(true));
      return;
    }
    const token = localStorage.getItem("rfd_token");
    if (!token) { setAuthReady(true); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => localStorage.removeItem("rfd_token")).finally(() => setAuthReady(true));
  }, []);

  const login = useCallback((token, u) => {
    localStorage.setItem("rfd_token", token);
    setUser(u);
  }, []);

  const loginWithGoogle = useCallback(() => {
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(`${window.location.origin}/dashboard`)}`;
  }, []);

  const logout = useCallback(() => {
    api.post("/auth/logout", {}, { withCredentials: true }).catch(() => {});
    localStorage.removeItem("rfd_token");
    setUser(null);
  }, []);

  const toggleLang = () => setLang((l) => (l === "ar" ? "en" : "ar"));
  const toggleCurrency = () => setCurrency((c) => (c === "USD" ? "IQD" : "USD"));

  const money = useCallback((usd) => {
    if (currency === "IQD") {
      const v = Math.round(usd * IQD_RATE);
      return `${v.toLocaleString("en-US")} د.ع`;
    }
    return `$${Number(usd).toLocaleString("en-US")}`;
  }, [currency]);

  return (
    <AppContext.Provider value={{ lang, setLang, toggleLang, t, currency, toggleCurrency, money, user, login, loginWithGoogle, logout, authReady, settings }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
